import { ReactElement, ReactNode, useEffect, useRef } from "react";
import { useApolloClient } from "@apollo/client";
import { Card, FilterGroups, FilterValues, RowActionProps, Table } from "@cofano/cck";
import type { FilterList, Highlights } from "@cofano/cck";
import { TableId, useBackendTable } from "components/table";
import useDeclarationTable from "view/declarations/utils/useDeclarationTable";
import { CharterType } from "__generated__/enums";

import {
	SearchDeclarationsForInvoicingDocument,
	type SearchDeclarationsForInvoicingQuery,
} from "__generated__/view/invoices/graphql/invoices.queries.generated";
import { ChildPropsFromPagination } from "types/commons";

export type DeclarationRow = ChildPropsFromPagination<SearchDeclarationsForInvoicingQuery, "searchDeclarations">;

type DeclarationsListProps = {
	tableId: TableId;
	selectSettings?: {
		onSelect: (rows: DeclarationRow[]) => void;
	};
	stagedIds?: number[];
	filterOverride?: FilterList;
	forceInvoiced?: boolean; // true = only show invoiced, false = only show not invoiced
	reloadToken?: number; // bump to force a refetch from the parent
	charterType: CharterType | null;
	highlightRow?: (row: DeclarationRow) => keyof typeof Highlights;
	rowActions?: Array<Omit<RowActionProps, "onClick" | "render" | "hidden" | "disabled"> & {
		onClick: (row: DeclarationRow) => void;
		hidden?: boolean | ((row: DeclarationRow) => boolean);
		disabled?: boolean | ((row: DeclarationRow) => boolean);
		render?: (row: DeclarationRow, refetch: () => void) => ReactNode | ReactElement;
	}>;
	onRowClick?: (row: DeclarationRow, refetch: () => void) => void;
};

const DeclarationsTable = ({
	tableId,
	selectSettings,
	stagedIds,
	filterOverride,
	forceInvoiced,
	reloadToken,
	charterType,
	highlightRow,
	rowActions = [],
	onRowClick,
}: DeclarationsListProps) => {

	const client = useApolloClient();
	const { filterFields, getSearchInput, getColumns } = useDeclarationTable(charterType);

	const { tableProps, filterProps, filterValuesProps, filters, refetch } = useBackendTable({
		id: tableId,
		selectSettings: selectSettings ? {
			selectionEnabled: true,
			isFullSelectEnabled: false,
		} : undefined,
		disableParams: true,
		onSelect: selectSettings ? (rows) => selectSettings.onSelect(rows as DeclarationRow[]) : undefined,
		onChange: async (pagination, sort, filterOriginal, callback) => {
			const filter = { ...filterOriginal };
			(filterOverride ?? []).forEach(o => {
				filter[o.property] = o.value;
			});

			try {
				const result = await client.query({
					query: SearchDeclarationsForInvoicingDocument,
					variables: {
						pagination: { ...pagination, sortList: sort },
						search: getSearchInput(filter, forceInvoiced, charterType),
					},
				});

				callback({
					content: result.data.searchDeclarations.content,
					totalResults: result.data.searchDeclarations.totalElements,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	const overrideKey = JSON.stringify(filterOverride ?? []);
	const isFirstOverrideRun = useRef(true);
	useEffect(() => {
		if (isFirstOverrideRun.current) {
			isFirstOverrideRun.current = false;
			return;
		}
		refetch();
	}, [ overrideKey, reloadToken ]);

	const overriddenProperties = new Set((filterOverride ?? []).map(o => o.property));
	const mergedFilterValues = [
		...filters.filter(f => !overriddenProperties.has(f.property)),
		...(filterOverride ?? []),
	];

	const hideInvoiced = forceInvoiced !== undefined;
	const fields = [
		filterFields.CharterNumber,
		...(charterType === CharterType.Purchase ? [ filterFields.SalesCharter ] : []),
		...(charterType === CharterType.Sales ? [ filterFields.PurchaseCharter ] : []),
		filterFields.ShipName,
		filterFields.CustomerReference,
		filterFields.DossierNumber,
		filterFields.Creditor,
		filterFields.Debtor,
		filterFields.DeclarationStatus,
		...(charterType === CharterType.Purchase ? [] : [ filterFields.SalesCharterStatus ]),
		...(charterType ? [] : [ filterFields.CharterType ]),
		filterFields.CharterSubtype,
		filterFields.BlDate,
		filterFields.Window,
		filterFields.InvoiceGroupType,
		...(hideInvoiced ? [] : [ filterFields.Invoiced ]),
	];
	const filterValueFields = fields.map(f => ({
		id: f.id,
		title: f.title,
		renderFilterValue: "renderFilterValue" in f ? f.renderFilterValue : undefined,
	}));

	return (
		<>
			<div style={{ marginBottom: 16 }}>
				<Card disablePadding>
					<FilterValues
						{...filterValuesProps}
						values={mergedFilterValues as typeof filterValuesProps.values}
						fields={filterValueFields} />
				</Card>
			</div>

			<Card disablePadding>
				<Table
					{...tableProps}
					content={tableProps.content.filter(row => !(stagedIds ?? []).includes(row.id as number))}
					filterComponent={() => (
						<FilterGroups
							{...filterProps}
							fields={fields} />
					)}
					columns={getColumns(refetch, hideInvoiced, charterType != null, charterType !== null, charterType === CharterType.Purchase, charterType === CharterType.Sales, charterType === CharterType.Purchase)}
					highlightRow={highlightRow}
					onRowClick={(clickedRow) => {
						onRowClick?.(clickedRow as DeclarationRow, refetch);
					}}
					rowActions={rowActions.map(action => {
						const render = action.render;
						return {
							...action,
							render: render ? (row) => (
								<span onClick={(e) => e.stopPropagation()}>
									{render(row, refetch)}
								</span>
							) : undefined,
						};
					})}
				/>
			</Card>
		</>
	);
};

export default DeclarationsTable;
